import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useTheme,
  Alert,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Avatar,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ConstructionIcon from '@mui/icons-material/Construction';
import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'; // Added for Presupuesto
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../../context/AuthContext';
import { useObras } from '../../context/ObrasContext';
import ObraSelector from '../obras/ObraSelector';
import { AIAssistantWidget } from '../ai/AIAssistantWidget';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
  toolbarContent?: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children, toolbarContent }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { usuario, logout } = useAuth();
  const { obras, obraSeleccionada, setObraSeleccionada } = useObras();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const [bottomNavValue, setBottomNavValue] = useState(0);

  // Update bottom nav value based on current path
  React.useEffect(() => {
    const path = location.pathname;
    if (path === '/') setBottomNavValue(0);
    else if (path.includes('/materiales')) setBottomNavValue(1);
    else if (path.includes('/fotos')) setBottomNavValue(2);
    else if (path.includes('/documentos')) setBottomNavValue(3);
    else setBottomNavValue(4); // Menu or other
  }, [location.pathname]);

  const drawerContent = (
    <Box sx={{ mt: 3, px: 2 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 1,
        mb: 4
      }}>
        <Box
          component="img"
          src="/Logo.png"
          alt="Mat ByB Logo"
          sx={{
            height: 35,
            width: 35,
            borderRadius: '50%',
            objectFit: 'contain',
          }}
        />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: 'grey.900',
            fontSize: '0.95rem',
            letterSpacing: '-0.01em'
          }}
        >
          Mat ByB
        </Typography>
      </Box>
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/')}
            sx={{
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText
              primary="Inicio"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: 500
              }}
            />
          </ListItemButton>
        </ListItem>

        {usuario?.role === 'ADMIN' && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavigate('/obras')}
              sx={{
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'rgba(59, 130, 246, 0.08)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText
                primary="Obras"
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              />
            </ListItemButton>
          </ListItem>
        )}

        <Divider sx={{ my: 1, mx: 2 }} />

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/materiales')}
            sx={{
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
              <ConstructionIcon />
            </ListItemIcon>
            <ListItemText
              primary="Materiales"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: 500
              }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/fotos')}
            sx={{
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
              <PhotoLibraryIcon />
            </ListItemIcon>
            <ListItemText
              primary="Fotos"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: 500
              }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/herramientas')}
            sx={{
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
              <BuildIcon />
            </ListItemIcon>
            <ListItemText
              primary="Herramientas"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: 500
              }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/documentos')}
            sx={{
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
              <DescriptionIcon />
            </ListItemIcon>
            <ListItemText
              primary="Documentos"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: 500
              }}
            />
          </ListItemButton>
        </ListItem>



        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigate('/presupuesto')}
            sx={{
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
              <AttachMoneyIcon />
            </ListItemIcon>
            <ListItemText
              primary="Presupuesto"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: 500
              }}
            />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1, mx: 2 }} />

        {usuario?.role !== 'no asignado' && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavigate('/tareas')}
              sx={{
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'rgba(59, 130, 246, 0.08)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
                <AssignmentIcon />
              </ListItemIcon>
              <ListItemText
                primary="Tareas"
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              />
            </ListItemButton>
          </ListItem>
        )}



        {usuario?.role === 'ADMIN' && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavigate('/usuarios')}
              sx={{
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'rgba(59, 130, 246, 0.08)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
                <GroupIcon />
              </ListItemIcon>
              <ListItemText
                primary="Usuarios"
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {usuario?.role === 'ADMIN' && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavigate('/configuracion')}
              sx={{
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'rgba(59, 130, 246, 0.08)'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'grey.700', minWidth: 40 }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Configuración"
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
        
        <ListItem sx={{ mt: 4 }}>
          <ListItemButton
            onClick={logout}
            sx={{
              borderRadius: 1,
              color: 'error.main',
              '&:hover': {
                bgcolor: 'error.lighter'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'error.main', minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Cerrar Sesión"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: 500
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'primary.main',
          color: 'white',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.15)',
          background: 'linear-gradient(90deg, #1E40AF 0%, #2563EB 100%)',
          display: isMobile ? 'flex' : 'flex',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '70px' } }}>
          {/* Mobile Header Content */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src="/Logo.png" sx={{ width: 32, height: 32, bgcolor: 'white', p: 0.5 }} />
            </Box>
            <Box>
              {/* Placeholder for future mobile actions */}
            </Box>
          </Box>

          <IconButton
            color="inherit"
            aria-label="abrir menú"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { sm: 'none' }, // Show hamburger on mobile
              color: 'white'
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexGrow: 1
          }}>
            <Box
              component="img"
              src="/Logo.png"
              alt="Mat ByB Logo"
              sx={{
                height: 45,
                width: 45,
                borderRadius: '50%',
                objectFit: 'contain',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                backgroundColor: 'white',
                padding: '4px',
                display: { xs: 'none', sm: 'block' }
              }}
            />
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 600,
                color: 'white',
                fontSize: '1.1rem',
                letterSpacing: '-0.01em',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Gestión de Materiales
            </Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            gap: 3,
            alignItems: 'center',
            ml: 'auto',
            height: '100%'
          }}>
            {usuario?.role !== 'no asignado' && obras.length > 0 && (
              <Box sx={{
                minWidth: { xs: 150, sm: 250 },
                height: '40px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <ObraSelector
                  obras={obras}
                  obraSeleccionada={obraSeleccionada}
                  onChange={setObraSeleccionada}
                  usuario={usuario}
                />
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile Drawer - Triggered by Menu button in Bottom Nav */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#ffffff',
              borderRight: '1px solid',
              borderColor: 'grey.200',
              boxShadow: 'none',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(8px)',
              pt: 2
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop Permanent Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#ffffff',
              borderRight: '1px solid',
              borderColor: 'grey.200',
              boxShadow: 'none',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(8px)',
              pt: 2
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: '56px', sm: '70px' },
          mb: 0,
          bgcolor: '#f8fafc',
          minHeight: { xs: 'calc(100vh - 112px)', sm: 'calc(100vh - 70px)' }
        }}
      >
        {usuario?.role === 'no asignado' && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              py: 2,
              px: 3,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 171, 0, 0.05)',
              border: '1px solid',
              borderColor: 'warning.light',
              '& .MuiAlert-icon': {
                color: 'warning.main'
              }
            }}
          >
            <Box>
              <Typography
                variant="subtitle1"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: 'grey.900',
                  mb: 0.5
                }}
              >
                Usuario pendiente de asignación
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'grey.600',
                  lineHeight: 1.5
                }}
              >
                Por favor, espere hasta que un administrador le asigne un rol.
                Mientras tanto, su acceso al sistema es limitado.
              </Typography>
            </Box>
          </Alert>
        )}
        {children}
      </Box>
      <AIAssistantWidget />

      {/* Mobile Bottom Navigation */}

    </Box>
  );
};